import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Switch,
} from "@/components/ui";
import {
  CookieConsentCategories,
  CookieConsentRecord,
} from "./useCookieConsent";

type CookiePreferencesModalProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  initial: CookieConsentRecord | null;
  onSave: (categories: CookieConsentCategories) => void;
};

export function CookiePreferencesModal({
  open,
  onOpenChange,
  initial,
  onSave,
}: CookiePreferencesModalProps) {
  const [analytics, setAnalytics] = useState<boolean>(
    initial?.analytics ?? false
  );
  useEffect(() => {
    if (open) {
      setAnalytics(initial?.analytics ?? false);
    }
  }, [open, initial?.analytics]);

  function handleSave() {
    onSave({ essential: true, analytics });
    onOpenChange(false);
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Preferências de cookies</DialogTitle>
          <DialogDescription>
            Escolha quais categorias de cookies você aceita. Você pode revisar e
            alterar essa decisão a qualquer momento na nossa{" "}
            <Link
              to="/legal/privacidade"
              className="text-teal-600 underline underline-offset-2"
            >
              Política de Privacidade
            </Link>
            .
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <CategoryRow
            title="Essenciais"
            description="Necessários para o funcionamento do sistema (autenticação, navegação, segurança). Sempre ativos."
            checked
            disabled
          />
          <CategoryRow
            title="Analíticos (Google Analytics)"
            description="Ajudam a entender como o sistema é usado para que possamos melhorá-lo."
            checked={analytics}
            onCheckedChange={setAnalytics}
          />
        </div>

        <DialogFooter className="gap-2 sm:gap-2">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancelar
          </Button>
          <Button onClick={handleSave}>Salvar preferências</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

type CategoryRowProps = {
  title: string;
  description: string;
  checked: boolean;
  disabled?: boolean;
  onCheckedChange?: (checked: boolean) => void;
};

function CategoryRow({
  title,
  description,
  checked,
  disabled,
  onCheckedChange,
}: CategoryRowProps) {
  return (
    <div className="flex items-start justify-between gap-4 rounded-lg border border-zinc-200 p-4">
      <div className="space-y-1">
        <h3 className="text-sm font-semibold text-zinc-800">
          {title}
        </h3>
        <p className="text-xs text-zinc-600">
          {description}
        </p>
      </div>
      <Switch
        checked={checked}
        disabled={disabled}
        onCheckedChange={onCheckedChange}
        aria-label={title}
      />
    </div>
  );
}
